import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        To reduce GLB file size in Blender, lower the polygon count with a Decimate modifier, shrink oversized
        textures, and export with File &gt; Export &gt; glTF 2.0 using glTF Binary, JPEG or WebP images and Draco
        compression. Those three changes cover the things that make a GLB big: geometry, textures and animation.
      </p>
      <p>
        This tutorial covers each step and every export setting that affects size, for Blender 4.2 through 5.2 LTS
        (where a name changed between versions, both are given).
      </p>

      <h2>Before you start: see what you're exporting</h2>
      <p>
        Turn on <strong>Statistics</strong> in the Viewport Overlays dropdown (top right of the 3D Viewport). It shows
        object, vertex and face counts, which depend on what is selected. A model with millions of faces, or a
        single prop with hundreds of thousands, is where Decimate will help most.
      </p>
      <p>
        Expect the exported file to have more vertices than Blender reports. The Blender manual notes that
        discontinuous UVs and flat-shaded edges are split into separate vertices on export, so UV seams and sharp edges
        add to the count. After exporting, drop the GLB into our free{' '}
        <Link to="/gltf-validator">glTF validator</Link> to see the real vertex and triangle counts, draw calls and
        extensions used.
      </p>
      <p>
        Also note your texture resolutions: on most textured models the images, not the mesh, make up the bulk of the
        file (here's <Link to="/blog/reduce-glb-file-size">how to check what makes a GLB big</Link>).
      </p>

      <h2>Step 1: reduce polygon count with the Decimate modifier</h2>
      <p>
        Select the mesh, open the Modifier properties (the wrench tab), and choose Add Modifier &gt; Generate &gt;
        Decimate. The modifier shows the resulting face count as you adjust it. It has three modes:
      </p>
      <ul>
        <li>
          <strong>Collapse</strong> (the default) merges vertices progressively while trying to keep the shape.{' '}
          <strong>Ratio</strong> is the fraction of faces to keep: 0.5 keeps about half, 0.1 about a tenth. Use a{' '}
          <strong>Vertex Group</strong> to protect areas such as a face or logo, and <strong>Symmetry</strong> to keep
          both sides matching.
        </li>
        <li>
          <strong>Un-Subdivide</strong> reverses subdivision on grid-like topology. Two iterations undo one level of
          subdivision, so use even numbers.
        </li>
        <li>
          <strong>Planar</strong> dissolves faces on flat areas below an <strong>Angle Limit</strong>. It suits
          hard-surface, architectural and CAD models. <strong>Delimit</strong> options (Material, Seam, Sharp, UVs)
          stop it from dissolving edges you need.
        </li>
      </ul>
      <p>
        Decimate works best on meshes that are denser than they need to be: sculpts, 3D scans, photogrammetry, CAD
        imports and anything with an applied Subdivision Surface. It does little good on a carefully modeled low-poly
        mesh, where every vertex defines the shape.
      </p>
      <p>
        Lower the ratio until the silhouette or shading starts to break, then back off a little. Check in wireframe and
        with textures on, looking for stretching along UV seams. When you're happy, apply it from the dropdown arrow in
        the modifier header (Apply), or leave it live and let the exporter apply it (see Apply Modifiers below).
      </p>
      <Callout title="Watch your Subdivision Surface modifiers">
        <p>
          The exporter's Apply Modifiers option exports the evaluated mesh, after all modifiers. A Subdivision Surface
          modifier left on the stack multiplies the face count in the GLB. Lower its level or remove it before
          export.
        </p>
      </Callout>

      <h2>Step 2: shrink your textures</h2>
      <p>
        A 4096×4096 texture has four times the pixels of a 2048×2048 one and 16 times those of a 1024×1024. For most
        web and AR viewers, 2K is plenty for hero objects and 1K or less for small parts.
      </p>
      <p>To resize inside Blender:</p>
      <ol>
        <li>Open the texture in the Image Editor.</li>
        <li>Choose Image &gt; Resize and set Size X and Y (for example, from 4096 to 2048).</li>
        <li>Save it with Image &gt; Save As under a new name, so you keep the full-resolution original.</li>
      </ol>
      <p>
        Or resize the files in any image editor and relink them. The glTF exporter can't resize textures itself, so do
        it beforehand, or afterwards with a GLB compressor.
      </p>

      <h2>Step 3: clean up the scene</h2>
      <ul>
        <li>
          <strong>Delete what you don't ship:</strong> hidden reference meshes, high-poly bake sources, cameras,
          lights and helper objects. By default the exporter includes every object in the scene.
        </li>
        <li>
          <strong>Or limit the export:</strong> in the export dialog's Include panel, tick Selected Objects or Visible
          Objects under Limit to.
        </li>
        <li>
          <strong>Purge unused data:</strong> in Blender 4.2 and later, File &gt; Clean Up &gt; Purge Unused Data opens
          a dialog that removes unused data-blocks. This mostly tidies the .blend file; the exporter already skips
          unused images and textures unless you enable Unused images or Unused textures.
        </li>
      </ul>

      <h2>Step 4: choose the right glTF export settings</h2>
      <p>
        Open File &gt; Export &gt; glTF 2.0 (.glb/.gltf). These are the settings that affect file size:
      </p>
      <table>
        <thead>
          <tr>
            <th>Panel</th>
            <th>Setting</th>
            <th>Recommended</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Top of the dialog</td><td>Format</td><td>glTF Binary (.glb)</td></tr>
          <tr><td>Include</td><td>Limit to</td><td>Selected Objects or Visible Objects</td></tr>
          <tr><td>Data &gt; Mesh</td><td>Apply Modifiers</td><td>On, unless you need shape keys</td></tr>
          <tr><td>Data &gt; Mesh</td><td>UVs / Normals / Tangents</td><td>UVs on if textured, Normals on, Tangents off unless needed</td></tr>
          <tr><td>Data &gt; Material</td><td>Images</td><td>JPEG or WebP</td></tr>
          <tr><td>Data &gt; Material</td><td>Image Quality</td><td>75 (default) to 90</td></tr>
          <tr><td>Data &gt; Shape Keys</td><td>Shape Keys</td><td>Off if you don't use morph targets</td></tr>
          <tr><td>Data &gt; Armature</td><td>Export Deformation Bones Only</td><td>On, unless your app needs the control bones</td></tr>
          <tr><td>Data &gt; Compression (Draco Compression in 5.2)</td><td>Draco</td><td>On, level 6, default quantization</td></tr>
          <tr><td>Animation</td><td>Animation</td><td>Off if the model isn't animated</td></tr>
        </tbody>
      </table>
      <p>
        If you export often, tick <strong>Remember Export Settings</strong> so they are stored in the .blend file.
      </p>

      <h3>Apply Modifiers and shape keys</h3>
      <p>
        Apply Modifiers exports the mesh after modifiers such as Decimate and Mirror. Blender warns that it prevents
        exporting shape keys, so leave it off on models that rely on them. If you don't need shape keys at all, untick
        Data &gt; Shape Keys; each one stores its own vertex offsets, plus normals by default.
      </p>

      <h3>Images: Automatic, JPEG, WebP or None</h3>
      <ul>
        <li>
          <strong>Automatic</strong> (default) keeps PNGs as PNG and JPEGs as JPEG, so large PNG textures stay large.
        </li>
        <li>
          <strong>JPEG</strong> saves textures as JPEG, except images that need alpha, which stay PNG.
        </li>
        <li>
          <strong>WebP</strong> (Blender 4.0 and later) saves every texture as WebP without a PNG or JPEG fallback,
          usually the smallest option. The viewer must support <code>EXT_texture_webp</code>; three.js does.
        </li>
        <li>
          <strong>None</strong> exports materials without textures.
        </li>
      </ul>
      <p>
        Image Quality (default 75) sets the JPEG or WebP quality. Avoid Create WebP unless you need a fallback: it adds
        WebP copies alongside the original textures, so the GLB gets bigger, not smaller.
      </p>

      <h3>Draco compression settings</h3>
      <p>
        Tick the checkbox on the Compression panel (called Draco Compression in Blender 5.2) under Data. The settings:
      </p>
      <ul>
        <li>
          <strong>Compression Level</strong>: 0 is fastest, 6 is the most compression and the default. Higher values
          aren't supported.
        </li>
        <li>
          <strong>Quantization</strong>: bits per value for Position (default 14), Normal (10), Tex Coord (12), Color
          (10) and Generic (12). Fewer bits give a smaller file but less precision; 0 turns quantization off. If
          surfaces look stepped or parts no longer meet cleanly, raise Position to 16.
        </li>
      </ul>
      <p>
        Draco compresses the base mesh only. Shape key data and animation are written uncompressed. Your viewer also
        needs a Draco decoder; in three.js that means adding a <code>DRACOLoader</code> (see{' '}
        <Link to="/blog/threejs-load-glb">how to load a GLB in three.js</Link>). Our{' '}
        <Link to="/blog/draco-compression">Draco compression guide</Link> covers how much it saves.
      </p>
      <p>
        Blender 5.2 LTS also adds a <strong>Meshopt Compression</strong> panel, with a choice between the{' '}
        <code>EXT_meshopt_compression</code> and <code>KHR_meshopt_compression</code> extensions. The EXT version is
        the more widely supported of the two. Enable Draco or Meshopt, not both; our{' '}
        <Link to="/blog/draco-vs-meshopt">Draco vs Meshopt comparison</Link> helps you choose.
      </p>

      <h3>Animation settings that affect size</h3>
      <ul>
        <li>
          <strong>Animation</strong> (panel checkbox): untick it if the model doesn't need to move.
        </li>
        <li>
          <strong>Sampling Animations &gt; Sampling Rate</strong>: how often, in frames, animated values are evaluated.
          The default of 1 writes a key every frame; 2 halves the keyframes. Check fast motions for jitter.
        </li>
        <li>
          <strong>Optimize Animations &gt; Optimize Animation Size</strong>: on by default; removes duplicate keyframes.
          Leave it on.
        </li>
        <li>
          <strong>Export Deformation Bones Only</strong> (Data &gt; Armature): drops control and IK bones and bakes
          their effect into the deforming bones. It requires sampling to be on.
        </li>
        <li>
          <strong>Limit to Playback Range</strong> (Animation &gt; Rest &amp; Ranges): clips animations to the timeline's
          playback range.
        </li>
      </ul>
      <p>
        Remove actions you don't need, too. In the default Actions mode, the exporter includes each object's active
        action plus every action stashed on its NLA tracks.
      </p>

      <h2>Step 5: check the exported file</h2>
      <p>
        Load the GLB in the <Link to="/gltf-validator">glTF validator</Link>. It should show no errors, the vertex and
        triangle counts you expect, and <code>KHR_draco_mesh_compression</code> among the extensions if you enabled
        Draco. Then test it in your target viewer, where missing decoder support shows up.
      </p>
      <p>For repeated exports, the same settings can be scripted with Blender's Python API:</p>
      <CodeBlock language="python">{`import bpy

bpy.ops.export_scene.gltf(
    filepath="/path/to/model.glb",
    export_format='GLB',
    use_selection=True,
    export_apply=True,
    export_image_format='WEBP',
    export_image_quality=80,
    export_draco_mesh_compression_enable=True,
    export_draco_mesh_compression_level=6,
)`}</CodeBlock>

      <h2>When Blender's export isn't enough</h2>
      <p>
        Blender's exporter can't resize textures, write AVIF or KTX2 textures, or simplify meshes at export time. For
        that, post-process the GLB. Our <Link to="/">GLB compressor</Link> resizes textures, converts them to WebP or
        AVIF, applies Draco, and runs weld, duplicate-vertex removal and simplification passes in one upload, with
        batch uploads for many files. gltf-transform and gltfpack do the same on the command line. Our{' '}
        <Link to="/blog/reduce-glb-file-size">complete guide to reducing GLB file size</Link> compares the options.
      </p>
      <p>
        If you plan to post-process, export from Blender without Draco and let the last tool apply it. Compressing
        geometry twice quantizes it twice, and some tools decode Draco and write the file back uncompressed if you
        don't compress it again.
      </p>

      <h2>FAQ</h2>
      <h3>How do I reduce polygon count in Blender?</h3>
      <p>
        Add a Decimate modifier (Add Modifier &gt; Generate &gt; Decimate), keep it in Collapse mode and lower the Ratio
        until just before the shape starts to break. Apply it, or let the glTF exporter apply it with Apply Modifiers.
      </p>
      <h3>Does Blender's glTF exporter support Draco compression?</h3>
      <p>
        Yes, under Data &gt; Compression in the glTF 2.0 export dialog (Data &gt; Draco Compression in Blender 5.2). The
        default level and quantization bits are a good starting point.
      </p>
      <h3>Should I export textures as JPEG or WebP?</h3>
      <p>
        WebP is usually smaller, and JPEG works in more places. Use WebP if your viewer supports{' '}
        <code>EXT_texture_webp</code> (three.js does); otherwise pick JPEG, which keeps textures with alpha as PNG.
      </p>
      <h3>Why is my GLB bigger than expected?</h3>
      <p>
        The usual causes are 4K PNG textures exported as-is (Images set to Automatic), a Subdivision Surface modifier
        baked in by Apply Modifiers, hidden objects included in the export, and animation sampled on every frame.
      </p>
      <h3>What Decimate ratio should I use?</h3>
      <p>
        There's no universal value. Start at 0.5, check the model up close, then go lower. Dense scans and sculpts often
        survive much lower ratios than hand-modeled meshes.
      </p>
    </>
  );
}
